import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { casesAPI, organizationsAPI } from '../lib/api';
import { useDropzone } from 'react-dropzone';
import { Button } from '../components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Loader2,
  ArrowLeft,
  Upload,
  Download,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileSpreadsheet,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { cn } from '../lib/utils';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';

export default function BulkImportPage() {
  const { user, isSuperAdmin, organizationId, organizationName } = useAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [progress, setProgress] = useState(null);
  const [jobId, setJobId] = useState(null);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchOrganizations();
    } else {
      setSelectedOrgId(organizationId);
    }
  }, [isSuperAdmin, organizationId]);

  // Poll for progress updates
  useEffect(() => {
    let interval;
    if (jobId && uploading) {
      interval = setInterval(async () => {
        try {
          const response = await casesAPI.getImportStatus(jobId);
          setProgress(response.data);
          
          if (response.data.status === 'completed') {
            setUploading(false);
            setResult({
              total_rows: response.data.total_rows,
              successful: response.data.successful,
              failed: response.data.failed,
              duplicates_skipped: response.data.duplicates_skipped,
              errors: response.data.errors
            });
            
            if (response.data.successful > 0) {
              toast.success(`Successfully imported ${response.data.successful} cases`);
            }
            if (response.data.duplicates_skipped > 0) {
              toast.info(`${response.data.duplicates_skipped} duplicates skipped`);
            }
            if (response.data.failed > 0) {
              toast.warning(`${response.data.failed} rows failed to import`);
            }
            
            clearInterval(interval);
            setJobId(null);
          }
        } catch (error) {
          console.error('Failed to get import status:', error);
        }
      }, 500);
    }
    return () => clearInterval(interval);
  }, [jobId, uploading]);

  const fetchOrganizations = async () => {
    try {
      const response = await organizationsAPI.getAll();
      setOrganizations(response.data.filter(o => o.is_active));
    } catch (error) {
      toast.error('Failed to load organizations');
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const csvFile = acceptedFiles[0];
    if (csvFile) {
      if (!csvFile.name.endsWith('.csv')) {
        toast.error('Please upload a CSV file');
        return;
      }
      setFile(csvFile);
      setResult(null);
      setProgress(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv']
    },
    multiple: false
  });

  const handleDownloadTemplate = async () => {
    try {
      const response = await casesAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'case_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    if (isSuperAdmin && !selectedOrgId) {
      toast.error('Please select an organization');
      return;
    }

    setUploading(true);
    setResult(null);
    setProgress(null);

    try {
      // Start async import with progress tracking
      const response = await casesAPI.startBulkImport(file, selectedOrgId, skipDuplicates);
      setJobId(response.data.job_id);
      setProgress({
        total_rows: response.data.total_rows,
        processed_rows: 0,
        progress_percent: 0,
        status: 'processing'
      });
      toast.info(`Starting import of ${response.data.total_rows} rows...`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setResult(null);
    setProgress(null);
    setJobId(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />

      {/* Main Content */}
      <main className="ml-0 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/cases" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground font-body text-sm mb-4 transition-colors"
            data-testid="back-to-cases"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Cases
          </Link>
          <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight">
            Bulk Import Cases
          </h1>
          <p className="font-body text-muted-foreground">
            Upload a CSV file to import multiple cases at once
          </p>
        </div>

        <div className="max-w-3xl space-y-6">
          {/* Step 1: Download Template */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-primary text-white text-sm flex items-center justify-center">1</span>
                Download Template
              </CardTitle>
              <CardDescription className="font-body">
                Start with our CSV template to ensure your data is formatted correctly
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                variant="outline" 
                onClick={handleDownloadTemplate}
                className="font-body"
                data-testid="download-template-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download CSV Template
              </Button>
              <p className="mt-3 text-sm text-muted-foreground font-body">
                The template includes 51 fields across all categories: Personal (Name, DOB, Sex, Citizenship, SSN, A#), 
                Contact (Email, Phone, Address), Immigration (Service Type, USCIS Receipt, Nonimmigrant Status), 
                Biographic (Ethnicity, Race, Height, Weight), and Employment (Employer, Job Title, Hours/Week)
              </p>
            </CardContent>
          </Card>

          {/* Step 2: Select Organization (Super Admin only) */}
          {isSuperAdmin && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary text-white text-sm flex items-center justify-center">2</span>
                  Select Organization
                </CardTitle>
                <CardDescription className="font-body">
                  Choose which organization these cases belong to
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-w-md">
                  <Label className="font-body mb-2 block">Organization *</Label>
                  <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                    <SelectTrigger className="font-body bg-card" data-testid="org-select">
                      <SelectValue placeholder="Select an organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizations.map(org => (
                        <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {organizations.length === 0 && (
                    <p className="text-sm text-muted-foreground mt-2 font-body">
                      No organizations available. <Link to="/admin/organizations/new" className="text-primary hover:underline">Create one first</Link>.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Upload CSV */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-primary text-white text-sm flex items-center justify-center">
                  {isSuperAdmin ? '3' : '2'}
                </span>
                Upload CSV File
              </CardTitle>
              <CardDescription className="font-body">
                Upload your completed CSV file to import cases
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Duplicate Detection Toggle */}
              <div className="mb-6 p-4 rounded-lg bg-secondary/50 border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-body font-medium">Skip Duplicate Cases</Label>
                    <p className="font-body text-sm text-muted-foreground mt-1">
                      Automatically skip cases that already exist (matched by A-Number or Name + DOB)
                    </p>
                  </div>
                  <Switch
                    checked={skipDuplicates}
                    onCheckedChange={setSkipDuplicates}
                    data-testid="skip-duplicates-toggle"
                  />
                </div>
              </div>

              {/* Progress Bar */}
              {progress && uploading && (
                <div className="mb-6 p-4 rounded-lg bg-primary/5 border border-primary/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-body text-sm font-medium">
                      Importing cases...
                    </span>
                    <span className="font-body text-sm text-muted-foreground">
                      {progress.processed_rows} / {progress.total_rows} rows
                    </span>
                  </div>
                  <Progress value={progress.progress_percent} className="h-2" />
                  <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                    <span>{progress.progress_percent}% complete</span>
                    {progress.duplicates_skipped > 0 && (
                      <span className="flex items-center gap-1">
                        <Copy className="w-3 h-3" />
                        {progress.duplicates_skipped} duplicates skipped
                      </span>
                    )}
                  </div>
                </div>
              )}

              {!result ? (
                <>
                  <div 
                    {...getRootProps()} 
                    className={cn(
                      "dropzone rounded-lg p-8 text-center cursor-pointer border-2 border-dashed",
                      isDragActive ? "border-primary bg-primary/5" : "border-border",
                      file && "border-success bg-success/5"
                    )}
                    data-testid="dropzone"
                  >
                    <input {...getInputProps()} data-testid="file-input" />
                    {file ? (
                      <>
                        <FileSpreadsheet className="w-12 h-12 mx-auto text-success mb-3" />
                        <p className="font-body font-medium text-foreground">{file.name}</p>
                        <p className="font-body text-sm text-muted-foreground mt-1">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); resetUpload(); }}
                          className="mt-3 font-body text-muted-foreground"
                        >
                          Choose different file
                        </Button>
                      </>
                    ) : (
                      <>
                        <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                        <p className="font-body text-muted-foreground">
                          {isDragActive ? 'Drop your CSV file here...' : 'Drag & drop your CSV file here, or click to select'}
                        </p>
                        <p className="font-body text-sm text-muted-foreground mt-1">
                          Only .csv files are supported
                        </p>
                      </>
                    )}
                  </div>

                  <div className="mt-6 flex justify-end">
                    <Button 
                      onClick={handleUpload}
                      disabled={!file || uploading || (isSuperAdmin && !selectedOrgId)}
                      className="btn-lift bg-primary text-white font-body"
                      data-testid="upload-btn"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Import Cases
                        </>
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                /* Results */
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg bg-secondary text-center">
                      <p className="font-heading text-2xl font-bold text-foreground">{result.total_rows}</p>
                      <p className="font-body text-sm text-muted-foreground">Total Rows</p>
                    </div>
                    <div className="p-4 rounded-lg bg-success/10 text-center">
                      <p className="font-heading text-2xl font-bold text-success">{result.successful}</p>
                      <p className="font-body text-sm text-muted-foreground">Imported</p>
                    </div>
                    {result.duplicates_skipped > 0 && (
                      <div className="p-4 rounded-lg bg-amber-50 text-center">
                        <p className="font-heading text-2xl font-bold text-amber-600">{result.duplicates_skipped}</p>
                        <p className="font-body text-sm text-muted-foreground">Duplicates</p>
                      </div>
                    )}
                    <div className={cn(
                      "p-4 rounded-lg text-center",
                      result.failed > 0 ? "bg-error/10" : "bg-secondary"
                    )}>
                      <p className={cn(
                        "font-heading text-2xl font-bold",
                        result.failed > 0 ? "text-error" : "text-foreground"
                      )}>{result.failed}</p>
                      <p className="font-body text-sm text-muted-foreground">Failed</p>
                    </div>
                  </div>

                  {/* Success Message */}
                  {result.successful > 0 && (
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-success/10 border border-success/20">
                      <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />
                      <p className="font-body text-success">
                        Successfully imported {result.successful} case{result.successful !== 1 ? 's' : ''}!
                      </p>
                    </div>
                  )}

                  {/* Duplicates Info */}
                  {result.duplicates_skipped > 0 && (
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
                      <Copy className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      <p className="font-body text-amber-700">
                        {result.duplicates_skipped} duplicate case{result.duplicates_skipped !== 1 ? 's were' : ' was'} detected and skipped.
                      </p>
                    </div>
                  )}

                  {/* Errors */}
                  {result.errors.length > 0 && (
                    <div className="p-4 rounded-lg bg-error/5 border border-error/20">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-5 h-5 text-error" />
                        <p className="font-body font-medium text-error">
                          {result.errors.filter(e => e.type !== 'duplicate').length} error{result.errors.filter(e => e.type !== 'duplicate').length !== 1 ? 's' : ''} occurred
                        </p>
                      </div>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {result.errors.filter(e => e.type !== 'duplicate').slice(0, 10).map((err, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm">
                            <XCircle className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
                            <p className="font-body text-muted-foreground">
                              <span className="font-medium">Row {err.row}:</span> {err.error}
                            </p>
                          </div>
                        ))}
                        {result.errors.filter(e => e.type !== 'duplicate').length > 10 && (
                          <p className="font-body text-sm text-muted-foreground">
                            ...and {result.errors.filter(e => e.type !== 'duplicate').length - 10} more errors
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-4">
                    <Button 
                      variant="outline" 
                      onClick={resetUpload}
                      className="font-body"
                      data-testid="upload-another-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Another File
                    </Button>
                    <Link to="/cases">
                      <Button className="btn-lift bg-primary text-white font-body" data-testid="view-cases-btn">
                        View Cases
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tips */}
          <Card className="bg-secondary/30 border-0">
            <CardContent className="pt-6">
              <h3 className="font-heading font-semibold text-foreground mb-3">Import Tips</h3>
              <ul className="space-y-2 font-body text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  <span><strong>Name</strong> is the only required field - all others are optional</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  <span>Use <strong>YYYY-MM-DD</strong> format for dates (e.g., 2024-01-15)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  <span>Valid service types: Family Visa, Work Visa, Student Visa, Asylum, Green Card, Citizenship, DACA, Other</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  <span>Valid USCIS decisions: Pending, Approved, Denied, RFE</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
